import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CalendarClock, Repeat, User, UserX } from 'lucide-react'
import type { FilterFieldKey, FilterValue, TaskFilter } from '@/lib/types/filter'
import type { FilterTargetGroup } from '@/store/ProjectFilterStore'
import { useCycles, useProjects } from '@/lib/db/rxdb/hooks'
import { useWorkspaceMode } from '@/hooks/useWorkspaceMode'
import useLoginStore from '@/store/LoginStore'
import useProjectFilterStore from '@/store/ProjectFilterStore'
import { EMPTY_FILTERS } from '@/lib/types/filter'

/** The active cycle for this project, or for any project in this space. */
export function useCurrentCycleId(projectId: string, scope: 'project' | 'space'): string | undefined {
  const { cycles } = useCycles()
  const { projects } = useProjects({ isDeleted: false })
  return useMemo(() => {
    const scopeIds = new Set(
      scope === 'space'
        ? [projectId, ...projects.filter((p) => p.parentProjectId === projectId).map((p) => p.id)]
        : [projectId],
    )
    return cycles.find((c) => !c.isDeleted && c.status === 'active' && scopeIds.has(c.projectId))?.id
  }, [cycles, projects, projectId, scope])
}

export type QuickFilter = {
  id: 'mine' | 'overdue' | 'current_cycle' | 'unassigned'
  label: string
  icon: React.ComponentType<{ className?: string }>
  fieldKey: FilterFieldKey
  value: FilterValue
  /** Already applied exactly as this quick filter would apply it. */
  active: boolean
}

/**
 * One-click answers to the most common questions (design 9a): My tasks,
 * Overdue, Current cycle, Unassigned. Each is just a normal filter chip once
 * applied, so it can be edited or removed like any other.
 */
export function useQuickFilters(
  projectId: string,
  scope: 'project' | 'space' = 'project',
  /** Which OR group quick filters land in; 'new' starts one (design 9b). */
  target: { group?: FilterTargetGroup; lockedFields?: Array<FilterFieldKey> } = {},
) {
  const { t } = useTranslation('tasks')
  const userId = useLoginStore((s) => s.userId)
  const { isPersonal } = useWorkspaceMode()
  const filters = useProjectFilterStore((s) => s.filters[projectId] ?? EMPTY_FILTERS)
  const addFilter = useProjectFilterStore((s) => s.addFilter)
  const setValues = useProjectFilterStore((s) => s.setValues)
  const upsertFilters = useProjectFilterStore((s) => s.upsertFilters)
  const removeFilter = useProjectFilterStore((s) => s.removeFilter)
  const { group, lockedFields } = target
  const currentCycleId = useCurrentCycleId(projectId, scope)

  /** The chip this quick filter would be, if it's already there in the target group. */
  const appliedChip = useCallback(
    (fieldKey: FilterFieldKey, value: FilterValue): TaskFilter | undefined => {
      if (group === 'new') return undefined
      const f = filters.find((x: TaskFilter) => x.fieldKey === fieldKey && x.group === group)
      return f && f.operator === 'includes' && f.values.length === 1 && f.values[0] === value
        ? f
        : undefined
    },
    [filters, group],
  )
  const isApplied = useCallback(
    (fieldKey: FilterFieldKey, value: FilterValue) => !!appliedChip(fieldKey, value),
    [appliedChip],
  )

  const quickFilters = useMemo<Array<QuickFilter>>(() => {
    const list: Array<QuickFilter> = []
    if (!isPersonal && userId) {
      list.push({ id: 'mine', label: t('filters.quick.mine'), icon: User, fieldKey: 'assignee', value: userId, active: isApplied('assignee', userId) })
    }
    list.push({ id: 'overdue', label: t('filters.quick.overdue'), icon: CalendarClock, fieldKey: 'due', value: 'overdue', active: isApplied('due', 'overdue') })
    if (currentCycleId) {
      list.push({ id: 'current_cycle', label: t('filters.quick.current_cycle'), icon: Repeat, fieldKey: 'cycle', value: currentCycleId, active: isApplied('cycle', currentCycleId) })
    }
    if (!isPersonal) {
      list.push({ id: 'unassigned', label: t('filters.quick.unassigned'), icon: UserX, fieldKey: 'assignee', value: '', active: isApplied('assignee', '') })
    }
    return list
  }, [isPersonal, userId, currentCycleId, isApplied, t])

  /** Apply (or, if already applied, remove) a quick filter. */
  const toggleQuickFilter = useCallback(
    (qf: QuickFilter) => {
      const applied = appliedChip(qf.fieldKey, qf.value)
      if (applied?.id) {
        removeFilter(projectId, applied.id)
        return
      }
      if (group === 'new') {
        const id = addFilter(projectId, qf.fieldKey, { group: 'new', lockedFields })
        if (id) setValues(projectId, id, [qf.value])
        return
      }
      upsertFilters(projectId, [
        { fieldKey: qf.fieldKey, operator: 'includes', values: [qf.value], group },
      ])
    },
    [projectId, group, lockedFields, appliedChip, addFilter, setValues, upsertFilters, removeFilter],
  )

  return { quickFilters, toggleQuickFilter }
}
